import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import DebouncedField from "@/components/DebouncedField";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UsersRound, Plus, UserPlus, Check, X, Crown, Trash2, LogIn, Download, History, Flag, Users, ChevronRight, FileText, UserCheck, Settings, Copy, RefreshCw, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTeam } from "@/lib/team";
import { factionIcon } from "@/lib/game";

const ruid = () => Math.random().toString(36).slice(2, 10);

function RosterManager({ setup, updateSetup }) {
  const { user } = useAuth();
  const { activeTeam } = useTeam();
  const [busy, setBusy] = useState(false);
  if (!setup || !activeTeam) return null;

  const isCaptain = activeTeam.captain_id === user?.id;
  const youKey = setup.you_are || "A";
  const players = setup.players.filter((p) => p.team === youKey);
  const roster = (id, patch) => updateSetup({ ...setup, players: setup.players.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  const removePlayer = (id) => updateSetup({ ...setup, players: setup.players.filter((p) => p.id !== id) });
  const addPlayer = () => updateSetup({ ...setup, players: [...setup.players, { id: ruid(), name: "", faction: "", team: youKey, lists: [{ name: "", army: "", text: "" }] }] });

  // assignable members = captain + members
  const assignable = [{ id: activeTeam.captain_id, username: activeTeam.captain }, ...(activeTeam.members || [])];
  const nameOf = (uid) => assignable.find((m) => m.id === uid)?.username || "someone";
  const myPlayer = players.find((p) => p.user_id === user?.id);

  // claim a slot as me — release any other slot I held
  const claimSlot = (pid) => {
    updateSetup({
      ...setup,
      players: setup.players.map((p) => {
        if (p.id === pid) return { ...p, user_id: user.id };
        if (p.user_id === user.id) return { ...p, user_id: null };
        return p;
      }),
    });
    toast.success("You're linked to this player");
  };
  const releaseSlot = (pid) => roster(pid, { user_id: null });
  // captain assigns a specific member to a slot (clears that member elsewhere)
  const assignSlot = (pid, uid) => {
    const val = uid === "none" ? null : uid;
    updateSetup({
      ...setup,
      players: setup.players.map((p) => {
        if (p.id === pid) return { ...p, user_id: val };
        if (val && p.user_id === val) return { ...p, user_id: null };
        return p;
      }),
    });
  };

  // list editing
  const updateList = (pid, i, patch) => {
    const p = players.find((x) => x.id === pid);
    const lists = [...(p.lists || [])]; lists[i] = { ...lists[i], ...patch };
    roster(pid, { lists });
  };
  const addList = (pid) => { const p = players.find((x) => x.id === pid); roster(pid, { lists: [...(p.lists || []), { name: "", army: p.faction || "", text: "" }] }); };
  const removeList = (pid, i) => { const p = players.find((x) => x.id === pid); roster(pid, { lists: (p.lists || []).filter((_, idx) => idx !== i) }); };

  const downloadAllLists = async () => {
    const withLs = players.filter((p) => p.ls_id && p.ls_event_id);
    if (!withLs.length) return toast.error("No Longshanks-linked players — claim this team from an event first.");
    if (!window.confirm(`Download army lists for ${withLs.length} player(s) from Longshanks? Uses ~${withLs.length} scraper credits.`)) return;
    setBusy(true);
    let ok = 0; const byId = {};
    for (const p of withLs) {
      try {
        const res = await api.fetchList(p.ls_id, p.ls_event_id);
        if (res.status === "OK" && res.lists.length) { byId[p.id] = res.lists.map((l) => ({ name: l.name, army: p.faction, text: l.text })); ok += 1; }
      } catch (e) { console.debug("list fetch failed", e); }
    }
    updateSetup({ ...setup, players: setup.players.map((p) => (byId[p.id] ? { ...p, lists: byId[p.id] } : p)) });
    setBusy(false);
    toast[ok ? "success" : "error"](ok ? `Fetched lists for ${ok} player(s)` : "No lists found");
  };

  const canFetch = players.some((p) => p.ls_id && p.ls_event_id);

  return (
    <>
      <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5" data-testid="team-settings-card">
        <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-3 flex items-center gap-2"><Settings className="h-4 w-4 text-slate-400" /> Team settings · {activeTeam.name}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Event</Label>
            <Input data-testid="settings-event-name" value={setup.event_name || ""} onChange={(e) => updateSetup({ ...setup, event_name: e.target.value })}
              placeholder="WTC 2026" className="bg-slate-950/60 border-slate-700" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Round</Label>
            <Input data-testid="settings-round-name" value={setup.round_name || ""} onChange={(e) => updateSetup({ ...setup, round_name: e.target.value })}
              placeholder="Round 1" className="bg-slate-950/60 border-slate-700" />
          </div>
        </div>
      </Card>

      <Card className="bg-[#121824]/70 border-amber-600/30 p-4 sm:p-5" data-testid="roster-manager-card">
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          <h3 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2"><UserCheck className="h-4 w-4 text-amber-400" /> Your roster &amp; who's who</h3>
          {canFetch && (
            <Button data-testid="roster-download-lists" onClick={downloadAllLists} disabled={busy} size="sm" variant="outline"
              className="h-8 border-cyan-700/50 bg-cyan-950/30 text-cyan-300 hover:bg-cyan-900/40 text-xs">
              <FileText className="h-3.5 w-3.5 mr-1" /> {busy ? "Fetching…" : "Download all lists"}
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-3">Each player below is a seat on your team. Link yourself to your player, and the captain can assign teammates to theirs (usernames may differ from Longshanks names).</p>

        {!myPlayer && players.length > 0 && (
          <div className="rounded-lg border border-amber-600/40 bg-amber-950/20 p-3 mb-3 text-sm text-amber-200" data-testid="pick-me-banner">
            Which player are you? Tap <b>“This is me”</b> on your seat below.
          </div>
        )}

        <div className="space-y-2">
          {players.map((p) => {
            const mine = p.user_id === user?.id;
            const taken = p.user_id && !mine;
            return (
              <div key={p.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-3" data-testid="roster-row">
                <div className="flex items-start gap-2">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input data-testid="roster-name-input" value={p.name} onChange={(e) => roster(p.id, { name: e.target.value })} placeholder="Player name" className="bg-slate-950/60 border-slate-700 h-9" />
                    <div data-testid="roster-faction-locked" className="flex items-center gap-2 h-9 px-3 rounded-md bg-slate-950/40 border border-slate-800 text-sm text-slate-300" title="Faction is locked from the event submission">
                      {p.faction
                        ? <><img src={factionIcon(p.faction)} alt="" className="h-4 w-4 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} /><span className="truncate">{p.faction}</span></>
                        : <span className="text-slate-600">—</span>}
                    </div>
                  </div>
                  <Button data-testid="roster-remove-player" variant="ghost" size="icon" onClick={() => removePlayer(p.id)} className="text-slate-500 hover:text-red-400 shrink-0 h-9 w-9"><Trash2 className="h-4 w-4" /></Button>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {p.user_id
                    ? <span className={`text-[11px] font-mono rounded px-2 py-1 flex items-center gap-1 ${mine ? "bg-emerald-500/15 text-emerald-300 border border-emerald-600/40" : "bg-slate-800/70 text-slate-300 border border-slate-700"}`} data-testid="roster-assigned-badge"><UserCheck className="h-3 w-3" />{mine ? "You" : nameOf(p.user_id)}</span>
                    : <span className="text-[11px] font-mono rounded px-2 py-1 bg-slate-950/60 text-slate-500 border border-slate-800" data-testid="roster-unclaimed-badge">Unclaimed seat</span>}

                  {!taken && !mine && <Button data-testid="roster-claim-me" onClick={() => claimSlot(p.id)} size="sm" className="h-7 bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold text-xs">This is me</Button>}
                  {mine && <Button data-testid="roster-release-me" onClick={() => releaseSlot(p.id)} size="sm" variant="outline" className="h-7 border-slate-700 bg-transparent text-slate-400 text-xs">Not me</Button>}

                  {isCaptain && (
                    <div className="ml-auto flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">Assign</span>
                      <Select value={p.user_id || "none"} onValueChange={(v) => assignSlot(p.id, v)}>
                        <SelectTrigger data-testid="roster-assign-select" className="bg-slate-950/60 border-slate-700 h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— unassigned —</SelectItem>
                          {assignable.map((m) => (<SelectItem key={m.id} value={m.id}>{m.username}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-2 pl-1">
                  {(p.lists || []).map((l, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">L{i + 1}</span>
                        <DebouncedField value={l.name} onCommit={(val) => updateList(p.id, i, { name: val })} placeholder="List name / caster" className="bg-slate-950/60 border-slate-800 h-8 text-xs" />
                        <button onClick={() => removeList(p.id, i)} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                      <DebouncedField as="textarea" value={l.text || ""} onCommit={(val) => updateList(p.id, i, { text: val })} placeholder="Paste full army list text here (viewable in the resolver)…" className="bg-slate-950/60 border-slate-800 text-[11px] font-mono h-16" data-testid="roster-list-text" />
                    </div>
                  ))}
                  {(p.lists || []).length < 2 && (
                    <button onClick={() => addList(p.id)} className="text-xs text-slate-500 hover:text-amber-400 flex items-center gap-1 font-medium"><Plus className="h-3 w-3" /> Add list</button>
                  )}
                </div>
              </div>
            );
          })}
          {players.length === 0 && <p className="text-xs text-slate-600 py-4 text-center">No players yet — claim your team from an event, or add players below.</p>}
        </div>
        <Button data-testid="roster-add-player" onClick={addPlayer} variant="outline" className="w-full mt-3 border-dashed border-slate-700 bg-transparent hover:bg-slate-800/50 text-slate-300"><UserPlus className="h-4 w-4 mr-2" /> Add player</Button>
      </Card>
    </>
  );
}


function CreateFromEvent({ onClaimed }) {
  const [lsUrl, setLsUrl] = useState("");
  const [paste, setPaste] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState(null);
  const [curEvent, setCurEvent] = useState(null); // {event_id, event_name}
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const loadEvents = () => api.events().then(setEvents).catch(() => {});
  useEffect(() => { loadEvents(); }, []);

  const persist = async (res, url) => {
    const id = res.event_id || url;
    if (!id) return;
    try {
      await api.saveEvent({ id, url: url || "", event_name: res.event_name || "", event_id: res.event_id || "", teams: res.teams });
      await loadEvents();
    } catch (e) { console.debug("saveEvent failed", e); }
  };

  const handleResult = (res, url) => {
    if (res.status === "OK" && res.teams.length) {
      setTeams(res.teams);
      setCurEvent({ event_id: res.event_id || "", event_name: res.event_name || "" });
      setNote("");
      setShowPaste(false);
      persist(res, url);
      toast.success(`Found ${res.teams.length} teams — pick yours below`);
    } else {
      setTeams(null);
      setNote(res.reason || "No teams found.");
      if (res.status === "BLOCKED") setShowPaste(true);
    }
  };

  const fetchByUrl = async () => {
    if (!lsUrl.trim()) return toast.error("Paste a Longshanks event URL first");
    setBusy(true); setNote("");
    try { handleResult(await api.longshanks({ url: lsUrl.trim() }), lsUrl.trim()); }
    catch { setNote("Fetch failed — try pasting the page source instead."); setShowPaste(true); }
    finally { setBusy(false); }
  };

  const parsePaste = async () => {
    if (!paste.trim()) return toast.error("Paste the event page source first");
    setBusy(true); setNote("");
    try { handleResult(await api.longshanks({ html: paste }), ""); }
    catch { setNote("Could not parse the pasted page."); }
    finally { setBusy(false); }
  };

  const loadSavedEvent = async (ev) => {
    setBusy(true);
    try {
      const full = await api.getEvent(ev.id);
      setTeams(full.teams || []);
      setCurEvent({ event_id: full.event_id || "", event_name: full.event_name || "" });
      setNote("");
      toast.success(`Loaded ${full.event_name || "event"} (${(full.teams || []).length} teams)`);
    } catch { toast.error("Could not load saved event"); }
    finally { setBusy(false); }
  };

  const claim = async (t) => {
    try {
      const res = await api.claimTeam({
        team_name: t.name,
        event_name: curEvent?.event_name || "",
        event_id: curEvent?.event_id || "",
        country: t.country || "",
        players: (t.players || []).map((p) => ({
          name: p.name, faction: p.faction, ls_id: p.ls_id || null,
          ls_event_id: (curEvent && curEvent.event_id) || p.ls_event_id || null,
          lists: p.lists || [],
        })),
      });
      onClaimed(res, t.name);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="bg-[#121824]/70 border-cyan-700/30 p-4 sm:p-5" data-testid="create-from-event-card">
      <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-1 flex items-center gap-2">
        <Download className="h-4 w-4 text-cyan-400" /> Create a team from your event
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        Teams are built before the event and submitted to Longshanks. Load your event, find your team in the list, and claim it — this creates a shared workspace with your roster already filled in. You become captain; if a teammate already claimed it, you'll request to join.
      </p>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="create-event-url-input" value={lsUrl} onChange={(e) => setLsUrl(e.target.value)}
          placeholder="https://www.longshanks.org/event/37180/" className="bg-slate-950/60 border-slate-700 flex-1" />
        <Button data-testid="create-event-fetch-button" onClick={fetchByUrl} disabled={busy}
          className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 font-semibold">
          {busy ? "Fetching…" : "Load event"}
        </Button>
      </div>

      {note && <p className="text-xs text-amber-400/90 mt-3 leading-relaxed">{note}</p>}

      <div className="mt-3">
        <button data-testid="create-toggle-paste-button" onClick={() => setShowPaste((v) => !v)}
          className="text-xs text-cyan-400 hover:text-cyan-300 font-medium">
          {showPaste ? "▾ Hide paste import" : "▸ Longshanks blocking you? Paste the page source instead"}
        </button>
        {showPaste && (
          <div className="mt-2 space-y-2">
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Open the event in your browser, press <b>Ctrl/Cmd+U</b>, select all (<b>Ctrl/Cmd+A</b>), copy, and paste here.
            </p>
            <Textarea data-testid="create-paste-input" value={paste} onChange={(e) => setPaste(e.target.value)}
              placeholder="Paste the full event page HTML here…" className="bg-slate-950/60 border-slate-700 h-28 font-mono text-xs" />
            <Button data-testid="create-parse-button" onClick={parsePaste} disabled={busy}
              className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 font-semibold">Parse pasted page</Button>
          </div>
        )}
      </div>

      {events.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-800">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Saved events — load instantly (no credits)</p>
          <div className="flex flex-wrap gap-2" data-testid="create-saved-events">
            {events.map((ev) => (
              <Button key={ev.id} data-testid="create-saved-event-button" onClick={() => loadSavedEvent(ev)} disabled={busy}
                size="sm" variant="outline" className="h-8 border-slate-700 bg-slate-900/40 text-slate-300 hover:bg-slate-800 text-xs">
                {ev.event_name || `Event ${ev.event_id || ev.id}`} <span className="text-slate-500 ml-1">· {ev.team_count ?? 0}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {teams && (
        <div className="mt-4 pt-4 border-t border-slate-800" data-testid="create-teams-list">
          <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Claim your team</p>
          <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
            {teams.map((t, i) => (
              <div key={`${t.name}-${i}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center gap-3" data-testid="create-team-row">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-cyan-400 shrink-0" />
                    <p className="text-sm font-semibold text-slate-100 truncate">{t.name}</p>
                    {t.country && <span className="text-[11px] text-slate-500 font-mono flex items-center gap-1"><Flag className="h-3 w-3" />{t.country}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 pl-6">
                    {t.players.map((p, j) => (
                      <span key={`${p.name}-${j}`} className="text-[11px] text-slate-400 flex items-center gap-1">
                        <img src={factionIcon(p.faction)} alt="" className="h-3.5 w-3.5 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
                        {p.name}
                      </span>
                    ))}
                  </div>
                </div>
                <Button data-testid="claim-team-button" onClick={() => claim(t)} size="sm"
                  className="bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold shrink-0">
                  Claim this team <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function TeamsManager({ setup, updateSetup }) {
  const { user } = useAuth();
  const { teams, activeId, setActiveId, activeTeam, loadTeams } = useTeam();
  const [directory, setDirectory] = useState([]);
  const [newName, setNewName] = useState("");
  const [newEvent, setNewEvent] = useState("");
  const [addName, setAddName] = useState("");
  const [showManual, setShowManual] = useState(false);
  const [joinCode, setJoinCode] = useState("");

  const refresh = async () => { await loadTeams(); api.teamDirectory().then(setDirectory).catch(() => {}); };
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const guard = (fn) => async (...a) => { try { await fn(...a); await refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const onClaimed = async (res, name) => {
    await refresh();
    if (res.status === "created") { setActiveId(res.team.id); toast.success(`Team "${name}" claimed — you are captain`); }
    else if (res.status === "member") { setActiveId(res.team.id); toast.success(`You're already in "${name}" — opened it`); }
    else { toast.success(`"${name}" already exists — join request sent to its captain`); }
  };

  const create = guard(async () => {
    if (!newName.trim()) return toast.error("Name your team");
    const t = await api.createTeam(newName.trim(), newEvent.trim());
    setNewName(""); setNewEvent(""); setActiveId(t.id);
    toast.success(`Team "${t.name}" created — you are captain`);
  });
  const apply = guard(async (tid) => { const r = await api.applyTeam(tid); toast.success(r.status === "member" ? "Already a member" : "Application sent — waiting for captain"); });
  const approve = guard(async (tid, uid) => { await api.approveMember(tid, uid); toast.success("Member approved"); });
  const reject = guard(async (tid, uid) => { await api.rejectMember(tid, uid); });
  const addByName = guard(async (tid) => { if (!addName.trim()) return; await api.addMember(tid, addName.trim()); setAddName(""); toast.success("Member added"); });
  const remove = guard(async (tid, uid) => { await api.removeMember(tid, uid); });
  const makeCaptain = guard(async (tid, uid) => { if (!window.confirm("Hand over captaincy? You become a regular member.")) return; await api.setCaptain(tid, uid); toast.success("Captain reassigned"); });
  const del = guard(async (tid) => { if (!window.confirm("Delete this team and all its data?")) return; await api.deleteTeam(tid); toast.success("Team deleted"); });
  const join = guard(async () => { if (!joinCode.trim()) return toast.error("Enter an invite code"); const r = await api.joinByCode(joinCode.trim()); setJoinCode(""); setActiveId(r.team.id); toast.success(r.status === "member" ? `You're already in "${r.team.name}"` : `Joined "${r.team.name}"`); });
  const regen = guard(async (tid) => { if (!window.confirm("Generate a new invite code? The old code will stop working.")) return; const r = await api.regenerateCode(tid); toast.success(`New invite code: ${r.invite_code}`); });
  const copyCode = (code) => { try { navigator.clipboard.writeText(code); toast.success("Invite code copied"); } catch { toast.error("Copy failed — code: " + code); } };

  const isCaptain = (t) => t.captain_id === user?.id;

  return (
    <div className="space-y-5">
      <Card className="bg-[#121824]/70 border-slate-800 p-4 flex items-center gap-3">
        <UsersRound className="h-5 w-5 text-amber-400" />
        <div>
          <h3 className="font-display font-bold uppercase tracking-wide text-sm">Teams</h3>
          <p className="text-xs text-slate-500">A team is a shared, live workspace (matrix, ratings & resolver). Claim yours from your event, or create one manually.</p>
        </div>
      </Card>

      <CreateFromEvent onClaimed={onClaimed} />

      <Card className="bg-[#121824]/70 border-emerald-700/30 p-4 sm:p-5" data-testid="join-by-code-card">
        <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-1 flex items-center gap-2"><KeyRound className="h-4 w-4 text-emerald-400" /> Join a team with an invite code</h3>
        <p className="text-xs text-slate-500 mb-3">Got a code from a teammate? Enter it to join their team instantly — no approval needed.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid="join-code-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="e.g. 7KQP2M"
            className="bg-slate-950/60 border-slate-700 flex-1 font-mono tracking-widest uppercase" />
          <Button data-testid="join-code-button" onClick={join} className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold"><LogIn className="h-4 w-4 mr-1.5" /> Join</Button>
        </div>
      </Card>

      {activeTeam && setup && <RosterManager setup={setup} updateSetup={updateSetup} />}


      <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5">
        <button data-testid="toggle-manual-create" onClick={() => setShowManual((v) => !v)}
          className="text-xs text-slate-400 hover:text-slate-200 font-medium flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> {showManual ? "Hide manual create" : "Or create a blank team manually (no event)"}
        </button>
        {showManual && (
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <Input data-testid="new-team-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Team name (e.g. USA Pinnacles)" className="bg-slate-950/60 border-slate-700 flex-1" />
            <Input data-testid="new-team-event" value={newEvent} onChange={(e) => setNewEvent(e.target.value)} placeholder="Event (optional)" className="bg-slate-950/60 border-slate-700 sm:w-48" />
            <Button data-testid="create-team-button" onClick={create} className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 font-semibold">Create</Button>
          </div>
        )}
      </Card>

      <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5" data-testid="my-teams-card">
        <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-3">My teams</h3>
        {teams.length === 0 && <p className="text-xs text-slate-500">You're not in any team yet — claim yours from an event above or apply to one below.</p>}
        <div className="space-y-3">
          {teams.map((t) => (
            <div key={t.id} className={`rounded-lg border p-3 ${t.id === activeId ? "border-amber-600/50 bg-amber-950/10" : "border-slate-800 bg-slate-900/40"}`} data-testid="my-team-row">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-slate-100">{t.name}</p>
                {isCaptain(t) && <span className="text-[10px] font-mono bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded flex items-center gap-1"><Crown className="h-3 w-3" /> CAPTAIN</span>}
                {t.event_name && <span className="text-[11px] text-slate-500 font-mono">· {t.event_name}</span>}
                <div className="flex-1" />
                {t.id !== activeId && <Button data-testid="activate-team-button" onClick={() => setActiveId(t.id)} size="sm" variant="outline" className="h-7 border-cyan-700/50 text-cyan-300 bg-transparent hover:bg-cyan-950/30"><LogIn className="h-3.5 w-3.5 mr-1" /> Open</Button>}
                {isCaptain(t) && <Button data-testid="delete-team-button" onClick={() => del(t.id)} size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>}
              </div>

              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="invite-code-block">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">Invite code</span>
                <code className="text-[12px] font-mono tracking-widest bg-slate-950/70 border border-slate-700 rounded px-2 py-0.5 text-emerald-300" data-testid="invite-code-value">{t.invite_code || "——"}</code>
                <button data-testid="copy-code-button" title="Copy invite code" onClick={() => copyCode(t.invite_code)} className="text-slate-400 hover:text-emerald-300"><Copy className="h-3.5 w-3.5" /></button>
                {isCaptain(t) && <button data-testid="regen-code-button" title="Generate a new code (revokes the old one)" onClick={() => regen(t.id)} className="text-slate-400 hover:text-amber-300"><RefreshCw className="h-3.5 w-3.5" /></button>}
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                <span className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-600/30 rounded px-1.5 py-0.5 flex items-center gap-1"><Crown className="h-3 w-3" />{t.captain}</span>
                {t.members.map((m) => (
                  <span key={m.id} className="text-[11px] text-slate-300 bg-slate-800/60 border border-slate-700 rounded px-1.5 py-0.5 flex items-center gap-1">
                    {m.username}
                    {isCaptain(t) && (
                      <>
                        <button data-testid="make-captain-button" title="Make captain" onClick={() => makeCaptain(t.id, m.id)} className="text-amber-400 hover:text-amber-300"><Crown className="h-3 w-3" /></button>
                        <button title="Remove" onClick={() => remove(t.id, m.id)} className="text-slate-500 hover:text-red-400"><X className="h-3 w-3" /></button>
                      </>
                    )}
                  </span>
                ))}
              </div>

              {isCaptain(t) && t.pending.length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-800">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Join requests</p>
                  <div className="flex flex-wrap gap-1.5">
                    {t.pending.map((p) => (
                      <span key={p.id} className="text-[11px] text-cyan-200 bg-cyan-950/30 border border-cyan-700/40 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid="pending-member">
                        {p.username}
                        <button data-testid="approve-member-button" title="Approve" onClick={() => approve(t.id, p.id)} className="text-emerald-400 hover:text-emerald-300"><Check className="h-3 w-3" /></button>
                        <button title="Reject" onClick={() => reject(t.id, p.id)} className="text-slate-500 hover:text-red-400"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {isCaptain(t) && (
                <div className="mt-2 flex gap-2">
                  <Input data-testid="add-member-input" value={t.id === activeId ? addName : ""} onChange={(e) => setAddName(e.target.value)} placeholder="Add member by username" className="bg-slate-950/60 border-slate-700 h-8 text-xs flex-1" onFocus={() => setActiveId(t.id)} />
                  <Button data-testid="add-member-button" onClick={() => addByName(t.id)} size="sm" variant="outline" className="h-8 border-slate-700 text-slate-300 bg-transparent"><UserPlus className="h-3.5 w-3.5 mr-1" /> Add</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-[#121824]/70 border-slate-800 p-4 sm:p-5" data-testid="team-directory-card">
        <h3 className="font-display font-bold uppercase tracking-wide text-sm mb-3">All teams — apply to join</h3>
        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {directory.map((t) => (
            <div key={t.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-center gap-3" data-testid="directory-row">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-100 truncate">{t.name} <span className="text-[11px] text-slate-500 font-mono">· {t.member_count} members · cap {t.captain}</span></p>
                {t.event_name && <p className="text-[11px] text-slate-500 font-mono">{t.event_name}</p>}
              </div>
              {t.is_member ? <span className="text-[11px] text-emerald-400 font-mono">member</span>
                : t.is_pending ? <span className="text-[11px] text-cyan-400 font-mono">requested</span>
                : <Button data-testid="apply-team-button" onClick={() => apply(t.id)} size="sm" className="bg-amber-500 text-slate-900 hover:bg-amber-400 font-semibold h-8">Apply</Button>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
